import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert, Row, Col, Modal } from 'react-bootstrap';
import { 
  CourseProvider, 
  ProgressProvider, 
  SessionProvider,
  useAuth,
  useCourseContext,
  useProgressContext
} from '@context';
import { useAdaptiveFlow, useQuestionTimer } from '@hooks';
import { InteractiveQuestion, CourseSidebar, RemediationPanel } from '@components';
import { progressTracker } from '@services';
import { canViewCourse } from '@utils/permissions';

function normalizePercent(value) {
  if (value == null || value === '') return 0;

  const numeric = typeof value === 'string'
    ? Number.parseFloat(value.replace('%', '').trim())
    : Number(value);

  if (!Number.isFinite(numeric)) return 0;

  const percent = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
  return Math.max(0, Math.min(100, percent));
}

/**
 * Learning Session Component
 */
function LearningSessionContent() {
  const { course } = useCourseContext();
  const { progress, refreshProgress } = useProgressContext();
  const { user } = useAuth();
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [currentQuestionData, setCurrentQuestionData] = useState(null);
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [currentSection, setCurrentSection] = useState(null);
  const [questionsInRound, setQuestionsInRound] = useState(0);
  const [currentContentBlockIndex, setCurrentContentBlockIndex] = useState(0);
  const [isRoundComplete, setIsRoundComplete] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [questionQueue, setQuestionQueue] = useState([]);
  const [questionQueueIndex, setQuestionQueueIndex] = useState(0);
  const [activeRemediation, setActiveRemediation] = useState(null);
  const [isClosingRemediation, setIsClosingRemediation] = useState(false);
  const [newlyUnlockedSection, setNewlyUnlockedSection] = useState(null); // Flow 5: unlock celebration
  const [welcomeBackSection, setWelcomeBackSection] = useState(null); // Flow 2: resume welcome banner
  const QUESTIONS_PER_ROUND = 8; // 8-10 questions per round
  const sessionInitStartedRef = useRef(false);
  const pendingQuestionStepRef = useRef(false);
  const staleStepSnapshotRef = useRef(null);
  // Override base .learning-session cap (900px) with a responsive max width.
  const learningSessionContainerStyle = { maxWidth: 'min(1600px, 96vw)' };
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth < 992 : false
  ));

  const {
    step: adaptiveStep,
    loading: adaptiveLoading,
    error: adaptiveError,
    initializeSession: initializeAdaptiveSession,
    resumeToSection,
    submitAnswer: submitAdaptiveAnswer,
  } = useAdaptiveFlow(user?.id, courseId, {
    autoStart: false,
    autoAdvanceOnSubmit: false,
    baseURL: import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_ADAPTIVE_API_BASE_URL || '/api/v1/adaptive',
  });

  const { elapsedTime, start: startTimer, stop: stopTimer, reset: resetTimer } = useQuestionTimer();

  const [questionReason, setQuestionReason] = useState(null); // Track why question was selected
  const [questionSectionId, setQuestionSectionId] = useState(null); // Track which section question is from

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mobileMediaQuery = window.matchMedia('(max-width: 991.98px)');
    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mobileMediaQuery.matches);

    if (typeof mobileMediaQuery.addEventListener === 'function') {
      mobileMediaQuery.addEventListener('change', handleViewportChange);
      return () => mobileMediaQuery.removeEventListener('change', handleViewportChange);
    }

    mobileMediaQuery.addListener(handleViewportChange);
    return () => mobileMediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    if (progress?.currentSectionId && course && !isSessionStarted && !sessionInitStartedRef.current) {
      sessionInitStartedRef.current = true;
      initializeSession();
    }
  }, [progress, course]);

  const initializeSession = async () => {
    try {
      // Use ProgressTracker's getNextSectionToStudy which handles LINEAR vs ADAPTIVE mode
      // LINEAR mode: Returns first incomplete section (sequential progression)
      // ADAPTIVE mode: Returns lowest-mastery section (after all sections visited)
      
      const nextSection = await progressTracker.getNextSectionToStudy(user?.id, courseId);
      const sectionToUse = nextSection || course.sections[0];
      
      console.log('[initializeSession] 📍 Resuming section:', {
        sectionId: sectionToUse?.id,
        sectionTitle: sectionToUse?.title,
        sectionIndex: sectionToUse?.index,
        completedCount: progress?.completedSections?.length || 0,
        completedSectionIds: progress?.completedSections || [],
        unlockedSectionIds: progress?.unlockedSections || [],
        totalSections: course.sections.length,
        allSectionIds: course.sections.map(s => ({ id: s.id, index: s.index, title: s.title })),
      });
      
      setCurrentSection(sectionToUse);
      
      // Flow 2 (PRD): If this section has been visited before, flag it for the Welcome Back banner
      const sp = progress?.getSectionProgress?.(sectionToUse?.id);
      if (sp && (sp.timesStudied || 0) > 0) {
        setWelcomeBackSection(sectionToUse.title);
      } else {
        setWelcomeBackSection(null);
      }
      
      // Mark section visit start for timesStudied tracking
      if (user?.id && courseId && sectionToUse?.id) {
        await progressTracker.markSectionVisitStart(user.id, courseId, sectionToUse.id);
      }

      await initializeAdaptiveSession();
      setIsSessionStarted(true);
    } catch (err) {
      console.error('Error initializing session:', err);
      sessionInitStartedRef.current = false;
    }
  };

  /**
   * F6 (PRD): Click-to-switch sections in the sidebar.
   * Only unlocked sections are selectable. Resets content/question state
   * and resumes the adaptive flow at the chosen section.
   */
  const handleSectionSelect = async (sectionId) => {
    const section = course?.sections?.find(s => s.id === sectionId);
    if (!section) return;

    pendingQuestionStepRef.current = false;
    staleStepSnapshotRef.current = null;

    // Reset all question/round state
    setCurrentSection(section);
    setShowContent(true);
    setCurrentContentBlockIndex(0);
    setCurrentQuestionData(null);
    setQuestionQueue([]);
    setQuestionQueueIndex(0);
    setQuestionsInRound(0);
    setIsRoundComplete(false);
    setIsLoadingQuestions(false);
    setNewlyUnlockedSection(null);

    // Welcome back if previously visited
    const sp = progress?.getSectionProgress?.(section.id);
    if (sp && (sp.timesStudied || 0) > 0) {
      setWelcomeBackSection(section.title);
    } else {
      setWelcomeBackSection(null);
    }

    // Mark section visit start for timesStudied tracking
    if (user?.id && courseId && section.id) {
      await progressTracker.markSectionVisitStart(user.id, courseId, section.id);
    }

    // Resume adaptive flow to chosen section
    if (resumeToSection) {
      await resumeToSection(section.id);
    }
  };

  const renderSidebar = () => {
    if (isMobileViewport) {
      return null;
    }

    return (
      <Col style={{ flex: sidebarCollapsed ? '0 0 50px' : '0 0 160px' }} className="d-none d-lg-block">
        <CourseSidebar
          course={course}
          progress={progress}
          currentSection={currentSection}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onSectionSelect={handleSectionSelect}
        />
      </Col>
    );
  };

  // Auto-advance when a round ends — skip the intermediate "Continue Learning" screen
  // and go directly to the next section's content.
  useEffect(() => {
    if (isRoundComplete && !currentQuestionData) {
      handleContinueLearning();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoundComplete, currentQuestionData]);

  useEffect(() => {
    if (!adaptiveStep || showContent) return;

    // Ignore the step object that existed before we requested fresh questions.
    if (pendingQuestionStepRef.current && adaptiveStep === staleStepSnapshotRef.current) {
      return;
    }

    if (adaptiveStep.type === 'question_group' || adaptiveStep.type === 'spaced_review') {
      const questions = Array.isArray(adaptiveStep.questions) ? adaptiveStep.questions : [];
      if (questions.length > 0) {
        const firstQuestion = questions[0];
        setQuestionQueue(questions);
        setQuestionQueueIndex(0);
        setCurrentQuestionData(firstQuestion);
        setQuestionReason(adaptiveStep.type === 'spaced_review' ? 'review' : (firstQuestion?.reason || 'current'));
        setQuestionSectionId(firstQuestion?.sectionId || currentSection?.id || null);
        setIsLoadingQuestions(false);
        setIsRoundComplete(false);
        pendingQuestionStepRef.current = false;
        staleStepSnapshotRef.current = null;
        resetTimer();
        startTimer();
      }
    } else if (adaptiveStep.type === 'remediation_content') {
      const remediationSection = course?.sections?.find(s => s.id === adaptiveStep.remediationSection?.id);
      if (remediationSection) {
        setCurrentSection(remediationSection);
      }

      setShowContent(false);
      setActiveRemediation({
        section: adaptiveStep.remediationSection || remediationSection || null,
        context: adaptiveStep.context || null,
        message: adaptiveStep.message || null,
      });
      setIsLoadingQuestions(false);
      pendingQuestionStepRef.current = false;
      staleStepSnapshotRef.current = null;
    } else if (adaptiveStep.type === 'section_complete') {
      setCurrentQuestionData(null);
      setQuestionReason(null);
      setQuestionSectionId(null);
      setIsLoadingQuestions(false);
      setIsRoundComplete(true);
      pendingQuestionStepRef.current = false;
      staleStepSnapshotRef.current = null;
    }
  }, [adaptiveStep, showContent, course, currentSection, resetTimer, startTimer]);

  const handleCompleteRemediation = async () => {
    if (isClosingRemediation || !activeRemediation?.section?.id) {
      return;
    }

    const targetSectionId = activeRemediation.section.id;
    setIsClosingRemediation(true);
    setActiveRemediation(null);

    pendingQuestionStepRef.current = true;
    staleStepSnapshotRef.current = adaptiveStep;
    setIsLoadingQuestions(true);

    try {
      await resumeToSection(targetSectionId);
    } catch (err) {
      console.error('[LearningPage] Failed to resume after remediation', err);
      pendingQuestionStepRef.current = false;
      staleStepSnapshotRef.current = null;
      setIsLoadingQuestions(false);
    } finally {
      setIsClosingRemediation(false);
    }
  };

  const handleBeginQuestions = async () => {
    pendingQuestionStepRef.current = true;
    staleStepSnapshotRef.current = adaptiveStep;

    // Clear any stale question before showing the loading state.
    setCurrentQuestionData(null);
    setQuestionQueue([]);
    setQuestionQueueIndex(0);
    setQuestionReason(null);
    setQuestionSectionId(null);

    setShowContent(false);
    setIsLoadingQuestions(true);
    setWelcomeBackSection(null); // dismiss welcome banner once questions begin
    // Reset answered questions for new section if section changed
    setQuestionsInRound(0);
    setIsRoundComplete(false);
    
    console.log('[handleBeginQuestions] Starting questions for section:', {
      sectionId: currentSection?.id,
      sectionTitle: currentSection?.title,
    });
    
    // Give the hook a moment to update with new section ID
    // This ensures answered questions are cleared before selecting
    try {
      await new Promise(resolve => setTimeout(resolve, 0));
      await resumeToSection(currentSection?.id);
    } catch (err) {
      console.error('[handleBeginQuestions] Failed to load fresh question group', err);
      pendingQuestionStepRef.current = false;
      staleStepSnapshotRef.current = null;
      setIsLoadingQuestions(false);
    }
  };

  const handleNextContentBlock = async () => {
    const visibleBlocks = getVisibleContentBlocks();
    const totalBlocks = visibleBlocks.length;
    const currentBlock = visibleBlocks[currentContentBlockIndex];
    const currentBlockViewKey = currentBlock?.viewKey ?? currentContentBlockIndex;
    
    // Track content view
    if (user?.id && courseId && currentSection?.id) {
      await progressTracker.recordContentView(
        user.id, 
        courseId, 
        currentSection.id, 
        currentBlockViewKey
      );
    }
    
    if (currentContentBlockIndex + 1 < totalBlocks) {
      // Move to next content block
      setCurrentContentBlockIndex(prev => prev + 1);
    } else {
      // Last block completed, go directly to questions
      await handleBeginQuestions();
    }
  };

  /**
   * Determine if this is the section's first visit
   */
  const isFirstSectionVisit = () => {
    if (!currentSection?.id) return true;
    const sectionProgress = progress?.sectionProgress?.[currentSection.id];
    if (!sectionProgress) return true;

    const attemptedFromSectionProgress = Number(sectionProgress.questionsAttempted || 0);
    if (attemptedFromSectionProgress > 0) return false;

    const attemptedFromQuestionProgress = Array.isArray(currentSection?.questions)
      ? currentSection.questions.some((question) => {
        const questionProgress = progress?.getQuestionProgress?.(question?.id);
        return (questionProgress?.timesShown || 0) > 0;
      })
      : false;

    if (attemptedFromQuestionProgress) return false;

    const sectionScore = normalizePercent(
      sectionProgress.score ?? sectionProgress.percent_complete ?? 0,
    );

    return sectionScore <= 0;
  };

  const normalizeContentBlock = (block, index) => {
    const rawContent = block?.content
      ?? block?.text
      ?? block?.explanation
      ?? block?.body
      ?? block?.code
      ?? '';

    const fallbackViewKey = block?.id != null
      ? `block_${block.id}`
      : `block_${index}`;

    return {
      ...block,
      title: block?.title || block?.name || `Content ${index + 1}`,
      content: typeof rawContent === 'string' ? rawContent : String(rawContent || ''),
      showInInitial: block?.showInInitial !== false,
      showInReview: block?.showInReview === true,
      viewKey: block?.viewKey || fallbackViewKey,
    };
  };

  const formatBlockContentHtml = (content) => {
    const safeContent = typeof content === 'string' ? content : '';
    return safeContent
      .replace(/```python\n/g, '<pre><code>')
      .replace(/```/g, '</code></pre>');
  };

  /**
   * Get visible content blocks based on section state and content block settings
   */
  const getVisibleContentBlocks = () => {
    if (!currentSection?.examples) return [];
    const normalizedBlocks = currentSection.examples.map((block, index) => normalizeContentBlock(block, index));
    
    // If section doesn't have trackInitialContent enabled, show all blocks
    if (currentSection.trackInitialContent === false) {
      return normalizedBlocks;
    }
    
    const isFirstVisit = isFirstSectionVisit();
    
    console.log('[getVisibleContentBlocks]', {
      sectionId: currentSection.id,
      isFirstVisit,
      totalBlocks: normalizedBlocks.length,
      sectionProgress: progress?.sectionProgress?.[currentSection.id],
    });
    
    // Filter blocks based on showInInitial and showInReview flags
    const filtered = normalizedBlocks.filter(block => {
      // Default behavior for backwards compatibility
      const showInInitial = block.showInInitial !== false;  // Defaults to true
      const showInReview = block.showInReview === true;      // Defaults to false
      
      console.log('[getVisibleContentBlocks] Evaluating block:', {
        title: block.title,
        showInInitial,
        showInReview,
        isFirstVisit,
        willShow: isFirstVisit ? showInInitial : showInReview,
      });
      
      // On first visit: show blocks marked for initial
      if (isFirstVisit) {
        return showInInitial;
      }
      
      // On revisit: show blocks marked for review
      return showInReview;
    });
    
    console.log('[getVisibleContentBlocks] Result:', {
      filteredCount: filtered.length,
      blockTitles: filtered.map(b => b.title),
    });
    
    // CRITICAL FIX: If no review content available on revisit, fall back to initial content
    // This prevents getting stuck when there's no review-specific content
    if (filtered.length === 0 && !isFirstVisit) {
      console.log('[getVisibleContentBlocks] No review content found - falling back to initial content');
      const fallback = normalizedBlocks.filter(block => {
        const showInInitial = block.showInInitial !== false;
        return showInInitial;
      });
      return fallback;
    }
    
    return filtered;
  };

  const loadNextQuestion = async () => {
    const nextIndex = questionQueueIndex + 1;
    if (nextIndex >= questionQueue.length) {
      setCurrentQuestionData(null);
      setQuestionReason(null);
      setQuestionSectionId(null);
      setIsRoundComplete(true);
      return;
    }

    const nextQuestion = questionQueue[nextIndex];
    setQuestionQueueIndex(nextIndex);
    setCurrentQuestionData(nextQuestion);
    setQuestionReason(nextQuestion?.reason || 'current');
    setQuestionSectionId(nextQuestion?.sectionId || currentSection?.id || null);
    resetTimer();
    startTimer();
  };

  const handleSubmitAnswer = async ({
    questionId,
    userAnswer,
    isCorrect,
    hintsUsed,
    fiftyFiftyUsed = false,
    reviewContentUsed = false,
  }) => {
    const timeSpent = stopTimer();
    const isLastQuestionInSet = questionQueue.length > 0
      ? questionQueueIndex + 1 >= questionQueue.length
      : (questionsInRound + 1) >= QUESTIONS_PER_ROUND;

    try {
      // Submit through orchestrated adaptive API path
      const submitResult = await submitAdaptiveAnswer({
        questionId,
        sectionId: questionSectionId || currentSection?.id,
        tagIds: currentQuestionData?.tags || [],
        userAnswer,
        isCorrect,
        timeSpent,
        hintsUsed,
        fiftyFiftyUsed, // Pass 50/50 usage to progress tracker
        reviewContentUsed,
        isLastQuestion: isLastQuestionInSet,
      });

      if (submitResult?.performance?.remediationTriggered) {
        console.log('[LearningPage] Remediation triggered from repeated misses', {
          sectionId: questionSectionId || currentSection?.id,
          tags: submitResult?.performance?.remediationTags || [],
        });

        setIsLoadingQuestions(true);
        setQuestionQueue([]);
        setQuestionQueueIndex(0);
        setCurrentQuestionData(null);
        setQuestionReason(null);
        setQuestionSectionId(null);

        await resumeToSection(questionSectionId || currentSection?.id);
        setIsLoadingQuestions(false);
        return;
      }

      // Increment round counter
      const newRoundCount = questionsInRound + 1;
      setQuestionsInRound(newRoundCount);

      // Refresh progress and detect newly unlocked sections (Flow 5: celebration)
      const prevUnlockedSet = new Set(progress?.unlockedSections || []);
      const updatedProgress = await refreshProgress();
      const justUnlocked = (updatedProgress?.unlockedSections || []).filter(id => !prevUnlockedSet.has(id));
      if (justUnlocked.length > 0) {
        const unlockedSec = course?.sections?.find(s => s.id === justUnlocked[0]);
        if (unlockedSec) setNewlyUnlockedSection(unlockedSec);
      }
      // CRITICAL: Check if round is complete IMMEDIATELY after answering
      // This prevents loadNextQuestion from loading from wrong section
      if (isLastQuestionInSet) {
        console.log('[handleSubmitAnswer] Round complete - showing Next button', {
          questionsAnswered: newRoundCount,
          threshold: questionQueue.length || QUESTIONS_PER_ROUND,
          isLastQuestionInSet,
        });
        // Set flag to show "Next" button instead of auto-advancing
        setIsRoundComplete(true);
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      setIsLoadingQuestions(false);
    }
  };

  const handleNextQuestion = async () => {
    
    await loadNextQuestion();
  };

  const handleContinueLearning = async () => {
    // Reset round complete flag
    setIsRoundComplete(false);
    
    // Get fresh progress to check if section was auto-unlocked by the last submit
    const freshProgress = await refreshProgress();
    
    const orderedSections = [...(course?.sections || [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    const currIdx = orderedSections.findIndex(s => s.id === currentSection?.id);
    const unlockedSet = new Set(freshProgress?.unlockedSections || []);

    // Determine learning mode.
    // LINEAR: Not every section has been attempted yet → advance sequentially.
    // ADAPTIVE: All sections visited → revisit weakest section by mastery.
    // Use sectionProgress questionsAttempted (not completedSections which needs 85%) so this
    // transitions to ADAPTIVE after all sections have been visited, not after mastered.
    const allSectionsHaveBeenVisited = orderedSections.every(s => {
      const sp = freshProgress?.sectionProgress?.[s.id];
      if (!sp) return false;

      const attempted = Number(sp.questionsAttempted || 0) > 0;
      const hasScore = normalizePercent(sp.score ?? sp.percent_complete ?? 0) > 0;
      return attempted || hasScore;
    });

    let nextSection = null;

    if (!allSectionsHaveBeenVisited) {
      // LINEAR mode: find the first unlocked section AFTER the current one.
      // IMPORTANT: Do NOT use getNextSectionToStudy here. In linear mode it returns the
      // "frontier" (highest-index unlocked), which is the *current* section when the
      // section score hasn't yet reached the 85% completedSections threshold. That causes
      // the same-section loop we're explicitly fixing here.
      nextSection = orderedSections.slice(currIdx + 1).find(s => unlockedSet.has(s.id)) || null;

      if (!nextSection) {
        // Next section isn't unlocked yet (score < 30% threshold).
        // Stay on the current section so the user can accumulate more correct answers.
        console.log('[handleContinueLearning] LINEAR: next section not unlocked yet — looping current section for more practice');
        nextSection = currentSection;
      }
    } else {
      // ADAPTIVE mode: delegate to ProgressTracker (lowest mastery, excluding current).
      const adaptiveNext = await progressTracker.getNextSectionToStudy(user?.id, courseId, currentSection?.id);
      nextSection = adaptiveNext
        || orderedSections.find(s => unlockedSet.has(s.id))
        || orderedSections[0]
        || null;
    }

    if (!nextSection) {
      console.error('[handleContinueLearning] ERROR: Could not determine next section');
      return;
    }

    console.log('[handleContinueLearning] Section progression:', {
      mode: allSectionsHaveBeenVisited ? 'ADAPTIVE' : 'LINEAR',
      currentSectionId: currentSection?.id,
      currentSectionIndex: currIdx,
      nextSectionId: nextSection.id,
      nextSectionIndex: orderedSections.findIndex(s => s.id === nextSection.id),
      unlockedSectionCount: unlockedSet.size,
      allSectionsHaveBeenVisited,
    });

    // Mark section visit start for timesStudied tracking
    if (user?.id && courseId && nextSection?.id) {
      await progressTracker.markSectionVisitStart(user.id, courseId, nextSection.id);
    }
    
    // Reset local question queue state for new round
    setNewlyUnlockedSection(null);
    setQuestionQueue([]);
    setQuestionQueueIndex(0);
    
    // CRITICAL: Update current section BEFORE showing content
    // This ensures the selectNextQuestion hook uses the correct section ID
    setCurrentSection(nextSection);
    setShowContent(true);
    setCurrentContentBlockIndex(0);
    setQuestionsInRound(0);
    setIsLoadingQuestions(false);
    setCurrentQuestionData(null);
    pendingQuestionStepRef.current = false;
    staleStepSnapshotRef.current = null;
  };

  const remediationOverlay = activeRemediation?.section ? (
    <Modal
      show
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>Review Before Continuing</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <RemediationPanel
          section={activeRemediation.section}
          context={activeRemediation.context}
          message={activeRemediation.message}
          onComplete={handleCompleteRemediation}
          loading={isClosingRemediation}
        />
      </Modal.Body>
    </Modal>
  ) : null;

  const renderWithRemediationOverlay = (content) => (
    <>
      {content}
      {remediationOverlay}
    </>
  );

  if (!isSessionStarted) {
    return renderWithRemediationOverlay(
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Starting session...</span>
        </Spinner>
      </Container>
    );
  }

  // Show content before questions
  if (showContent && currentSection) {
    const visibleBlocks = getVisibleContentBlocks();
    const totalBlocks = visibleBlocks.length;
    const currentBlock = visibleBlocks[currentContentBlockIndex];
    
    // CRITICAL FIX: If no blocks are available for this visit type, skip directly to questions
    // This prevents the student from getting stuck when there's no review content
    if (totalBlocks === 0) {
      console.log('[LearningPage] No content blocks available - auto-progressing to questions', {
        sectionId: currentSection.id,
        isFirstVisit: isFirstSectionVisit(),
      });

      pendingQuestionStepRef.current = true;
      staleStepSnapshotRef.current = adaptiveStep;

      setCurrentQuestionData(null);
      setQuestionQueue([]);
      setQuestionQueueIndex(0);
      setQuestionReason(null);
      setQuestionSectionId(null);

      // Auto-progress to questions instead of showing error message
      setShowContent(false);
      setCurrentContentBlockIndex(0);
      setIsLoadingQuestions(true);
      // Use a setTimeout to ensure state updates are processed before loading question
      if (questionsInRound < QUESTIONS_PER_ROUND) {
        setTimeout(async () => {
          try {
            await resumeToSection(currentSection?.id);
          } catch (err) {
            console.error('[LearningPage] Auto-transition to questions failed', err);
            pendingQuestionStepRef.current = false;
            staleStepSnapshotRef.current = null;
            setIsLoadingQuestions(false);
          }
        }, 0);
      } else {
        console.log('[LearningPage] Round complete - waiting for section transition', {
          questionsInRound,
          threshold: QUESTIONS_PER_ROUND,
        });
        pendingQuestionStepRef.current = false;
        staleStepSnapshotRef.current = null;
        setIsLoadingQuestions(false);
      }
      return renderWithRemediationOverlay(
        <Container fluid className="learning-session-with-sidebar py-4">
          <Row className="g-3 flex-nowrap">
            {renderSidebar()}
            <Col style={{ flex: '1 1 auto' }}>
              <Container className="learning-session py-4" style={learningSessionContainerStyle}>
                <Card className="mb-4">
                  <Card.Header className="bg-primary text-white">
                    <h4 className="mb-0">{currentSection.title}</h4>
                  </Card.Header>
                  <Card.Body className="text-center py-5">
                    <Spinner animation="border" className="mb-3" />
                    <p>Loading questions...</p>
                  </Card.Body>
                </Card>
              </Container>
            </Col>
          </Row>
        </Container>
      );
    }
    
    if (currentBlock) {
      // Check if this content has been viewed before
      const sectionProgress = progress?.getSectionProgress?.(currentSection.id);
      const sectionTimesStudied = sectionProgress?.timesStudied || 0;
      const blockKey = currentBlock?.viewKey || `block_${currentContentBlockIndex}`;
      const viewCount = sectionProgress?.contentViews?.[blockKey]?.count || 0;
      const hasBeenViewed = sectionTimesStudied > 1 || viewCount > 0;
      const isReviewContentMode = currentSection.trackInitialContent !== false && !isFirstSectionVisit();
      
      // Show single content block
      return renderWithRemediationOverlay(
        <Container fluid className="learning-session-with-sidebar py-4">
          <Row className="g-3 flex-nowrap">
            {renderSidebar()}
            <Col style={{ flex: '1 1 auto' }}>
              <Container className="learning-session py-4" style={learningSessionContainerStyle}>
                <Card className="mb-4">
                  <Card.Header className="bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <h4 className="mb-0">{currentSection.title}</h4>
                      <small> {currentContentBlockIndex + 1} of {totalBlocks}</small>
                    </div>
                  </Card.Header>
                <Card.Body>
                  {/* Flow 2 (PRD): Welcome back banner shown once on resume */}
                  {welcomeBackSection && currentContentBlockIndex === 0 && (
                    <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
                      <span className="me-2" style={{ fontSize: '1.3rem' }}>👋</span>
                      <span><strong>Welcome back!</strong> Let’s review <em>{welcomeBackSection}</em>.</span>
                    </div>
                  )}

                  <p className="lead mb-4">{currentSection.description}</p>
                  
                  {/* Explicit review mode indicator when review content is being shown */}
                  {isReviewContentMode && (
                    <div className="alert alert-info mb-4" role="alert">
                      <strong>📖 Review Mode</strong> You are now reviewing this section before continuing.
                    </div>
                  )}

                  {/* Secondary indicator when this specific block was previously viewed */}
                  {!isReviewContentMode && hasBeenViewed && (
                    <div className="alert alert-info mb-4" role="alert">
                      <strong>📖 For your review</strong>
                    </div>
                  )}
                  
                  <Card className="mb-4 content-card">
                    <Card.Body>
                      <h5 className="text-primary mb-3">{currentBlock.title}</h5>
                      <div 
                        className="content-text"
                        style={{ whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{ __html: formatBlockContentHtml(currentBlock.content) }}
                      />
                    </Card.Body>
                  </Card>

                  <div className="text-center mt-4">
                    <Button 
                      variant="primary" 
                      size="lg"
                      onClick={handleNextContentBlock}
                    >
                      {currentContentBlockIndex + 1 === totalBlocks ? 'Begin Questions →' : 'Next →'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Container>
          </Col>
        </Row>
      </Container>
      );
    }
  }

  if (adaptiveError) {
    return renderWithRemediationOverlay(
      <Container fluid className="learning-session-with-sidebar py-4">
        <Row className="g-3 flex-nowrap">
          {renderSidebar()}
          <Col style={{ flex: '1 1 auto' }}>
            <Alert variant="danger">
              <h4>Error Loading Question</h4>
              <p>{adaptiveError}</p>
              <Button variant="outline-danger" onClick={() => resumeToSection(currentSection?.id)}>
                Try Again
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!currentQuestionData) {
    // Show loading state when transitioning to questions
    if (isLoadingQuestions) {
      return renderWithRemediationOverlay(
        <Container fluid className="learning-session-with-sidebar py-4">
          <Row className="g-3 flex-nowrap">
            {renderSidebar()}
            <Col style={{ flex: '1 1 auto' }}>
              <Card className="text-center">
                <Card.Body className="py-5">
                  <Spinner animation="border" className="mb-3" />
                  <p>Loading questions...</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      );
    }
    
    // Adaptive mode should not show a static completion card here.
    // Keep users in flow while the next adaptive step is resolved.
    return renderWithRemediationOverlay(
      <Container fluid className="learning-session-with-sidebar py-4">
        <Row className="g-3 flex-nowrap">
          {renderSidebar()}
          <Col style={{ flex: '1 1 auto' }}>
            <Card className="text-center">
              <Card.Body className="py-5">
                <Spinner animation="border" className="mb-3" />
                <p>
                  {(isRoundComplete || adaptiveStep?.type === 'section_complete')
                    ? 'Preparing the next section...'
                    : 'Preparing your next adaptive step...'}
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return renderWithRemediationOverlay(
    <Container fluid className="learning-session-with-sidebar py-4">
      <Row className="g-3 flex-nowrap">
        {renderSidebar()}
        <Col style={{ flex: '1 1 auto' }}>
          <Container className="learning-session py-4" style={learningSessionContainerStyle}>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">{course?.title}</h5>
              </div>
              <div className="text-end">
                <div className="timer">⏱️ {elapsedTime}s</div>
              </div>
            </div>

            <InteractiveQuestion
              question={currentQuestionData}
              userId={user?.id}
              courseId={courseId}
              onSubmit={handleSubmitAnswer}
              onNext={handleNextQuestion}
              onContinue={handleContinueLearning}
              showExplanation={true}
              disabled={adaptiveLoading}
              reason={questionReason}
              currentSectionId={currentSection?.id}
              questionSectionId={questionSectionId}
              course={course}
              timesShown={progress?.getQuestionProgress(currentQuestionData?.id)?.timesShown || 0}
              sectionMastery={progress?.getSectionProgress(questionSectionId || currentSection?.id)?.score || 0}
              isRoundComplete={isRoundComplete}
            />
          </Container>
        </Col>
      </Row>
    </Container>
  );
}

/**
 * Learning Page
 */
export function LearningPage() {
  const { courseId } = useParams();
  const { user } = useAuth();

  if (!user) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          Please log in to start learning.
        </Alert>
      </Container>
    );
  }

    if (!canViewCourse(user, courseId)) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
              Access denied: You do not have access to this course.
        </Alert>
      </Container>
    );
  }

  return (
    <CourseProvider courseId={courseId}>
      <ProgressProvider courseId={courseId}>
        <SessionProvider courseId={courseId}>
          <LearningSessionContent />
        </SessionProvider>
      </ProgressProvider>
    </CourseProvider>
  );
}
